import type { Provider } from '@latency-globe/shared';
import { forcedBoundaries, type RenderMode, type Store } from '../store.ts';
import { clear, el } from './dom.ts';

const WHAT: Record<Provider, string> = {
  cloudflare: 'Each cell is coloured by the Cloudflare location that served most consenting visitors from it. This is where the network meets you, not where anything runs.',
  aws: 'Each cell is coloured by the AWS region that answered fastest for most consenting visitors from it: visitor → edge → region, over AWS’s backbone.',
  gcp: 'Each cell is coloured by the Google Cloud region that answered fastest for most consenting visitors from it. GCP is measured through opaque responses; the weekly check verifies the endpoints instead.',
  azure: 'Each cell is coloured by the Azure region that answered fastest for most consenting visitors from it: visitor → edge → region, over Azure’s backbone.',
};

export function mountLegend(root: HTMLElement, store: Store, onTheme: (t: 'dark' | 'light') => void): void {
  const render = () => {
    const s = store.state;
    clear(root);
    const modeToggle = el('div', { class: 'toggle', role: 'group', 'aria-label': 'Render mode' });
    for (const [m, label] of [['field', 'Field'], ['boundaries', 'Boundaries']] as Array<[RenderMode, string]>) {
      const b = el('button', { type: 'button', 'aria-pressed': String(s.mode === m), text: label });
      b.addEventListener('click', () => store.set({ mode: m }));
      modeToggle.append(b);
    }
    const themeToggle = el('div', { class: 'toggle', role: 'group', 'aria-label': 'Theme' });
    for (const t of ['dark', 'light'] as const) {
      const b = el('button', { type: 'button', 'aria-pressed': String(s.theme === t), text: t === 'dark' ? 'Dark' : 'Light' });
      b.addEventListener('click', () => onTheme(t));
      themeToggle.append(b);
    }
    root.append(
      el('h2', { text: 'Reading the map' }),
      el('p', { text: WHAT[s.provider] }),
      el('div', { class: 'key' },
        el('span', { class: 'grad', 'aria-hidden': 'true' }), el('span', { text: 'colour = where you were served; neighbouring locations contrast' }),
        el('span', { class: 'seam', 'aria-hidden': 'true' }), el('span', { text: 'seam = catchment boundary between two locations' }),
        el('span', { class: 'fade', 'aria-hidden': 'true' }), el('span', { text: 'faint = few votes; solid = well attested' }),
      ),
      el('div', { class: 'row' }, modeToggle, themeToggle),
      el('details', {},
        el('summary', { text: 'What this is not' }),
        el('p', { text: 'Not a benchmark: numbers are not comparable between visitors and this is not a provider ranking. A cell’s colour is a vote, not a measurement.' }),
        el('p', { text: 'The Cloudflare location list grows only from consenting visits, so an unvisited location is indistinguishable from one that does not exist. Sparse areas are misleading until coverage fills in.' }),
        el('p', { text: 'Precise positions never leave your browser: only a ~5 km grid square is sent, and only after you press the button.' }),
      ),
    );
    if (forcedBoundaries()) root.querySelector<HTMLButtonElement>('.toggle button')!.disabled = true;
  };
  render();
  store.subscribe((_s, changed) => { if (changed.has('provider') || changed.has('mode') || changed.has('theme')) render(); });
}
